/** Normalise une URL pour comparaison (origin + pathname sans slash final). */
export function normalizePageUrl(url) {
  if (!url || typeof url !== 'string') return '';
  const trimmed = url.trim();
  if (!trimmed) return '';
  try {
    const parsed = new URL(trimmed);
    const path = parsed.pathname.replace(/\/+$/, '') || '/';
    return `${parsed.origin}${path === '/' ? '/' : path}`.toLowerCase();
  } catch {
    return trimmed.replace(/\/+$/, '').toLowerCase();
  }
}

export function getReferencePagesList(project) {
  if (!Array.isArray(project?.reference_pages)) return [];
  return project.reference_pages.filter((u) => typeof u === 'string' && u.trim());
}

/** Normalise une URL saisie (absolue ou relative au site du projet). */
export function normalizeReferencePageUrl(raw, projectUrl) {
  const input = (raw || '').trim();
  if (!input) return null;
  let href = input;
  if (!/^https?:\/\//i.test(href)) {
    const base = (projectUrl || '').trim();
    if (!base) return null;
    try {
      href = new URL(href, base.endsWith('/') ? base : `${base}/`).href;
    } catch {
      return null;
    }
  }
  try {
    const parsed = new URL(href);
    if (!['http:', 'https:'].includes(parsed.protocol)) return null;
    return parsed.href.replace(/\/+$/, '') || parsed.href;
  } catch {
    return null;
  }
}

/** Sélection courante + pages enregistrées, sans doublon (ordre : sélection puis sauvegardées). */
export function mergeReferencePageUrls(selected, saved) {
  const seen = new Set();
  const out = [];
  const add = (url) => {
    if (typeof url !== 'string' || !url.trim()) return;
    const trimmed = url.trim();
    const key = normalizePageUrl(trimmed);
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push(trimmed);
  };
  for (const u of selected || []) add(u);
  for (const u of saved || []) add(u);
  return out;
}

/** URLs canoniques pour l’audit (même format que le backend analyse-url). */
export function resolveReferenceUrlsForAudit(urls, projectUrl) {
  return (urls || [])
    .map((u) => normalizeReferencePageUrl(u, projectUrl) || (typeof u === 'string' ? u.trim() : ''))
    .filter((u) => u && /^https?:\/\//i.test(u));
}

export function getReferencePagesSet(project) {
  return new Set(getReferencePagesList(project).map(normalizePageUrl));
}

export function isReferencePage(url, project) {
  if (!url) return false;
  const set = getReferencePagesSet(project);
  if (!set.size) return false;
  return set.has(normalizePageUrl(url));
}

export function urlToPageSlug(url) {
  if (!url) return 'home';
  try {
    const u = new URL(url);
    const path = u.pathname.replace(/^\/+|\/+$/g, '');
    return path ? path.replace(/\//g, '___') : 'home';
  } catch {
    return encodeURIComponent(url);
  }
}

export function getSeoDetailPath(slug, pageUrl) {
  if (!slug || !pageUrl) return null;
  return `/projects/${slug}/seo/${urlToPageSlug(pageUrl)}`;
}

/** Trie les lignes : pages de référence en premier. */
export function sortRowsWithReferenceFirst(rows, project, urlKey = 'url') {
  if (!rows?.length) return rows || [];
  const refSet = getReferencePagesSet(project);
  if (!refSet.size) return rows;
  return [...rows].sort((a, b) => {
    const aRef = refSet.has(normalizePageUrl(a[urlKey]));
    const bRef = refSet.has(normalizePageUrl(b[urlKey]));
    if (aRef === bRef) return 0;
    return aRef ? -1 : 1;
  });
}

export const REFERENCE_ROW_CLASS =
  'bg-accent/[0.06] hover:bg-accent/[0.1] ring-1 ring-inset ring-accent/20';

/**
 * Données rapport pages de référence (synthèse, export JSON, etc.).
 */
export async function buildReferenceReportPages(slug, project) {
  const urls = getReferencePagesList(project);
  if (!slug || !urls.length) return [];

  const { getAudits, getTechnicalErrors } = await import('../api');
  const { flattenTechnicalErrors, isOperationalTechnicalError } = await import('./technicalErrors');

  const [audits, errorsData] = await Promise.all([
    getAudits(slug).catch(() => []),
    getTechnicalErrors(slug).catch(() => null),
  ]);

  const errorsByNorm = {};
  for (const err of flattenTechnicalErrors(errorsData, project)) {
    if (!err?.url || isOperationalTechnicalError(err)) continue;
    const norm = normalizePageUrl(err.url);
    if (!errorsByNorm[norm]) errorsByNorm[norm] = [];
    errorsByNorm[norm].push(err);
  }

  const seoByNorm = {};
  for (const row of audits || []) {
    if (!row?.url) continue;
    const norm = normalizePageUrl(row.url);
    seoByNorm[norm] = row;
    seoByNorm[row.url] = row;
  }

  return urls.map((url) => {
    const norm = normalizePageUrl(url);
    const seo = seoByNorm[norm] || seoByNorm[url] || null;
    return {
      url,
      seo,
      perf: seo,
      errors: errorsByNorm[norm] || [],
    };
  });
}
